import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import {
  User, RotateCcw, Trash2, FileText, Receipt, FolderArchive,
  ChevronDown, ChevronUp, Calendar, Loader2, Download,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatItDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("it-IT");
  } catch {
    return s;
  }
};

const StoricoDipendenteCard = ({ utente, isAdmin, getAuthHeaders, onRiattiva, onHardDelete }) => {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDocs = async () => {
    if (docs) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/utenti-disattivati/${utente.id}/documenti-finali`, {
        headers: getAuthHeaders(),
      });
      setDocs(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento documenti");
    }
    setLoading(false);
  };

  const toggle = () => {
    setOpen((o) => !o);
    if (!docs) loadDocs();
  };

  const downloadFile = (path, nome) => {
    if (!path) return;
    const url = path.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = nome || "documento";
    a.target = "_blank";
    a.click();
  };

  const meseNome = (m) => ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"][m - 1] || "—";

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid={`storico-${utente.id}`}>
      {/* Header — sempre visibile */}
      <div className="p-4 opacity-90">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-foreground truncate">{utente.nome || utente.username}</h3>
                <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">
                  Disattivato
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {utente.email && <>{utente.email} · </>}
                {utente.telefono && <>{utente.telefono} · </>}
                disattivato il {formatItDate(utente.disattivato_at)}
                {utente.disattivato_da_nome && <> da {utente.disattivato_da_nome}</>}
              </p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRiattiva(utente.id)}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                data-testid={`riattiva-${utente.id}`}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Riattiva
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onHardDelete(utente.id, utente.nome || utente.username)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid={`hard-delete-${utente.id}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Elimina definitivamente
              </Button>
            </div>
          )}
        </div>

        {/* Toggle documenti */}
        <button
          type="button"
          onClick={toggle}
          className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`toggle-docs-${utente.id}`}
        >
          <FolderArchive className="h-3.5 w-3.5" />
          Documenti finali e storico
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Sezione collassabile */}
      {open && (
        <div className="border-t border-border bg-muted/30 p-4 space-y-3" data-testid={`docs-${utente.id}`}>
          {loading && (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              <span className="text-xs">Caricamento documenti...</span>
            </div>
          )}

          {docs && !loading && (
            <>
              {/* Stats periodo lavorato */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2.5 rounded-xl bg-background text-center">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Presenze</p>
                  <p className="text-lg font-extrabold text-foreground">{docs.stats.presenze_totali}</p>
                </div>
                <div className="p-2.5 rounded-xl bg-background text-center">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Buste paga</p>
                  <p className="text-lg font-extrabold text-foreground">{docs.stats.buste_totali}</p>
                </div>
                <div className="p-2.5 rounded-xl bg-background text-center">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Contratti</p>
                  <p className="text-lg font-extrabold text-foreground">{docs.stats.contratti_totali}</p>
                </div>
              </div>

              {(docs.stats.prima_presenza || docs.stats.ultima_presenza) && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-background px-3 py-2 rounded-lg">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>
                    Attivo dal <strong className="text-foreground">{formatItDate(docs.stats.prima_presenza)}</strong>
                    {" al "}
                    <strong className="text-foreground">{formatItDate(docs.stats.ultima_presenza)}</strong>
                  </span>
                </div>
              )}

              {/* Ultima busta paga */}
              <div className="p-3 rounded-xl bg-background border border-border">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-50 flex-shrink-0">
                    <Receipt className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Ultima busta paga
                    </p>
                    {docs.ultima_busta ? (
                      <>
                        <p className="text-sm font-semibold text-foreground mt-0.5">
                          {meseNome(docs.ultima_busta.mese)} {docs.ultima_busta.anno}
                          {docs.ultima_busta.netto != null && (
                            <span className="text-muted-foreground ml-2 font-normal">
                              · netto € {Number(docs.ultima_busta.netto).toFixed(2)}
                            </span>
                          )}
                        </p>
                        {docs.ultima_busta.file_path && (
                          <Button
                            size="sm"
                            variant="link"
                            className="h-auto p-0 text-xs"
                            onClick={() => downloadFile(docs.ultima_busta.file_path, `busta-${docs.ultima_busta.anno}-${docs.ultima_busta.mese}.pdf`)}
                            data-testid={`download-busta-${utente.id}`}
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Scarica PDF
                          </Button>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground italic mt-0.5">Nessuna busta paga emessa</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Ultimo contratto */}
              <div className="p-3 rounded-xl bg-background border border-border">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 rounded-lg bg-blue-50 flex-shrink-0">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Ultimo contratto
                    </p>
                    {docs.ultimo_contratto ? (
                      <>
                        <p className="text-sm font-semibold text-foreground mt-0.5">
                          {docs.ultimo_contratto.tipo || "Contratto"}
                          {docs.ultimo_contratto.data_inizio && (
                            <span className="text-muted-foreground ml-2 font-normal">
                              · dal {formatItDate(docs.ultimo_contratto.data_inizio)}
                              {docs.ultimo_contratto.data_fine && (
                                <> al {formatItDate(docs.ultimo_contratto.data_fine)}</>
                              )}
                            </span>
                          )}
                        </p>
                        {docs.ultimo_contratto.file_path && (
                          <Button
                            size="sm"
                            variant="link"
                            className="h-auto p-0 text-xs"
                            onClick={() => downloadFile(docs.ultimo_contratto.file_path, `contratto-${utente.nome || "ex"}.pdf`)}
                            data-testid={`download-contratto-${utente.id}`}
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Scarica PDF
                          </Button>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground italic mt-0.5">Nessun contratto registrato</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StoricoDipendenteCard;
